
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import fs from 'fs'
import type {MDXRemoteSerializeResult} from 'next-mdx-remote'
import {serialize} from 'next-mdx-remote/serialize'
import useTranslation from 'next-translate/useTranslation'
import {useRouter} from 'next/router'
import {join} from 'path'
import Container from '../../components/atoms/container'
import GridContainer from '../../components/atoms/grid-container'
import GridItem from '../../components/atoms/grid-item'
import Heading from '../../components/atoms/heading'
import HighlightedSection from '../../components/atoms/highlighted-section'
import LoadableImage from '../../components/atoms/loadable-image'
import CallToAction from '../../components/molecules/call-to-action'
import InfoBox from '../../components/molecules/info-box'
import PageCover from '../../components/organisms/page-cover'
import CaseStudySection from '../../components/templates/case-study-section'
import {
  FooterDataStateProps,
  FooterProvider,
} from '../../contexts/footer-context'
import PageLayout from '../../layouts/page-layout'
import fetchGeneralSettings from '../../lib/fetch-general-settings'
import fetchServicesData from '../../lib/fetch-services-data'
import fetchSocialLinks from '../../lib/fetch-social-links'
import mapRawCaseStudyToCaseStudy from '../../lib/map-raw-case-study-to-case-study'
import bishopSymbol from '../../public/assets/bishop-logo-symbol.png'
import {CaseStudy, Service as ServiceDataProps} from '../../types'

export default function ServiceDetails({
  coverPhoto,
  coverPhotoAlt,
  title,
  description,
  metaDescription,
  previewImage,
  pathname,
  technologies,
  technologiesSectionTitle,
  developmentProcessSectionTitle,
  developmentProcessStepsPhoto,
  developmentProcessStepsPhotoAlt,
  developmentProcessSteps,
  caseStudies,
  reasons,
  social,
  general,
}: ServiceDataProps<MDXRemoteSerializeResult> & FooterDataStateProps) {
  const router = useRouter()
  const {t} = useTranslation('services')

  if (router.isFallback) {
    return <></>
  }

  return (
    <FooterProvider value={{social, general}}>
      <PageLayout
        className="grid grid-flow-row gap-16 md:gap-24 pt-24 lg:pt-10 pb-12 md:pb-24"
        disableDefaultPadding
        title={title}
        description={metaDescription}
        openGraph={{
          images: [{url: previewImage || bishopSymbol.src}],
          url: `services/${pathname}`,
        }}
        prefixImageUrl={!previewImage}
        type={'article'}
      >
        <div>
          <PageCover
            className="sm:min-h-180"
            title={
              <Heading
                variant="h1"
                disableMaxWidth
                className="text-center lg:text-left font-black"
              >
                {title}
              </Heading>
            }
            description={description}
            media={
              <LoadableImage
                src={coverPhoto}
                alt={coverPhotoAlt}
                layout="responsive"
                width={558}
                height={460}
                objectFit="cover"
                borderRadius="rounded-none sm:rounded-3xl"
                wrapperClassName="max-w-none sm:max-w-xl lg:max-w-none -mx-6 -mt-4 sm:mx-auto sm:mt-0"
                sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                priority
              />
            }
          />

          {technologies && (
            <HighlightedSection>
              <Heading
                variant="h2"
                className="text-left md:text-center"
                disableMaxWidth
              >
                {technologiesSectionTitle}
              </Heading>

              <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
                {technologies.map(({title, body}) => (
                  <InfoBox title={title} key={title}>
                    {body}
                  </InfoBox>
                ))}
              </GridContainer>
            </HighlightedSection>
          )}
        </div>

        {developmentProcessSteps && (
          <Container className="grid gap-8 justify-items-start md:justify-items-center">
            <Heading
              variant="h2"
              className="hidden md:block text-center"
              disableMaxWidth
            >
              {developmentProcessSectionTitle}
            </Heading>

            <GridContainer
              disableDefaultColumns
              className="items-center grid-flow-row md:grid-flow-col grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 w-full"
            >
              <GridItem className="grid gap-4 md:gap-8 order-2 md:order-1">
                <Heading
                  variant="h2"
                  className="block md:hidden text-center"
                  disableMaxWidth
                >
                  {developmentProcessSectionTitle}
                </Heading>

                <GridContainer
                  disableDefaultColumns
                  className="gap-y-8 gap-x-12 w-full"
                >
                  {developmentProcessSteps.map(({title, body}) => (
                    <InfoBox title={title} key={title}>
                      {body}
                    </InfoBox>
                  ))}
                </GridContainer>
              </GridItem>

              <GridItem className="order-1 md:order-2">
                <LoadableImage
                  src={developmentProcessStepsPhoto}
                  alt={developmentProcessStepsPhotoAlt}
                  layout="responsive"
                  width={558}
                  height={460}
                  objectFit="cover"
                  borderRadius="rounded-2xl md:rounded-3xl"
                  sizes="(max-width: 576px) 576px, (max-width: 640px) 828px, 640px"
                />
              </GridItem>
            </GridContainer>
          </Container>
        )}

        {reasons && (
          <HighlightedSection>
            <Heading
              variant="h2"
              className="text-left md:text-center"
              disableMaxWidth
            >
              {t('reasons.title')}
            </Heading>

            <GridContainer className="md:grid-cols-3 gap-y-8 gap-x-12 w-full">
              {reasons.map(({title, body}) => (
                <InfoBox title={title} key={title}>
                  {body}
                </InfoBox>
              ))}
            </GridContainer>
          </HighlightedSection>
        )}

        <Container>
          <CallToAction
            title={t('contact.title')}
            callToActionText={t('common:navigation.contact-us')}
            href="/contact-us"
          >
            {t('contact.body')}
          </CallToAction>
        </Container>

        {caseStudies && (
          <CaseStudySection
            title={t('case-studies.title')}
            caseStudies={caseStudies}
          />
        )}
      </PageLayout>
    </FooterProvider>
  )
}

 async function _getStaticProps({...ctx}) {
  try {
    const {reasons} = fetchServicesData()
    const {slug} = ctx.params
    const parseFrontMatter = await import(`../../lib/parse-front-matter`)
    const content = await import(`../../content/available-services/${slug}.md`)
    const {attributes: data} = parseFrontMatter.default<any>(content.default)
    const caseStudies: Array<string> = data['case-studies'] || null

    const availableCaseStudies = caseStudies
      ? await Promise.all(
          caseStudies.map(
            caseStudy => import(`../../content/case-studies/${caseStudy}.md`),
          ),
        )
      : null

    const mappedCaseStudies = availableCaseStudies
      ? availableCaseStudies
          .map(
            content =>
              parseFrontMatter.default<CaseStudy>(content.default).attributes,
          )
          .map(mapRawCaseStudyToCaseStudy)
      : null

    const coverPhotoParts = data['cover-photo'].split(/\/v[0-9]{1,}\//i)
    const developmentProcessStepsPhotoParts =
      data['development-process-steps-photo'].split(/\/v[0-9]{1,}\//i)

    const description = await serialize(data.description)

    return {
      props: {
        coverPhoto: coverPhotoParts[coverPhotoParts.length - 1],
        coverPhotoAlt: data['cover-photo-alt'],
        title: data.title,
        description,
        metaDescription: data['meta-description'],
        previewImage: data['preview-image'] || null,
        pathname: slug,
        technologiesSectionTitle: data['technologies-section-title'] || null,
        technologies: data.technologies || null,
        developmentProcessSectionTitle:
          data['development-process-section-title'] || null,
        developmentProcessStepsPhoto:
          developmentProcessStepsPhotoParts[
            developmentProcessStepsPhotoParts.length - 1
          ],
        developmentProcessStepsPhotoAlt:
          data['development-process-steps-photo-alt'] || null,
        developmentProcessSteps: data['development-process-steps'] || null,
        caseStudies: mappedCaseStudies,
        reasons,
        social: fetchSocialLinks(),
        general: fetchGeneralSettings(),
      },
    }
  } catch {
    return {
      notFound: true,
    }
  }
}

export async function getStaticPaths() {
  const JOB_LISTINGS = join(process.cwd(), 'content/available-services')
  const paths = fs
    .readdirSync(JOB_LISTINGS)
    .map(path => path.replace(/\.mdx?$/, ''))
    .map(slug => ({params: {slug}}))

  return {
    paths,
    fallback: true,
  }
}


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/services/[slug]',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  